import React, { forwardRef, useMemo } from "react";
import { Box, Switch, Typography } from "@mui/material";
import { theme } from "../../theme/semanticTheme";

/**
 * Preset color maps for the thumb (checked) and track — extend as product needs grow.
 */
const VARIANT_STYLES = {
  accent: {
    checkedThumb: theme.colors.purple,
    checkedTrack: `${theme.colors.purple}99`,
  },
  success: {
    checkedThumb: theme.colors.success,
    checkedTrack: `${theme.colors.success}99`,
  },
  error: {
    checkedThumb: theme.colors.error,
    checkedTrack: `${theme.colors.error}80`,
  },
  info: {
    checkedThumb: theme.colors.info,
    checkedTrack: `${theme.colors.info}99`,
  },
  /** Uses MUI default primary styling only — no custom theme override */
  default: null,
};

function buildToggleSx({ variant, checkedColor, trackColor, sx }) {
  const custom = checkedColor || trackColor;
  if (custom) {
    return {
      "& .MuiSwitch-switchBase.Mui-checked": {
        color: checkedColor ?? theme.colors.purple,
      },
      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: trackColor ?? `${theme.colors.purple}99`,
      },
      ...sx,
    };
  }
  const preset = VARIANT_STYLES[variant];
  if (!preset) {
    return sx ?? {};
  }
  return {
    "& .MuiSwitch-switchBase.Mui-checked": { color: preset.checkedThumb },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: preset.checkedTrack,
    },
    ...sx,
  };
}

/** Background colors for the wide pill when `showTrackLabels` is on */
function getLabeledTrackPalette({ variant, checkedColor, inactiveTrackColor }) {
  if (checkedColor && inactiveTrackColor) {
    return { on: checkedColor, off: inactiveTrackColor };
  }
  if (checkedColor) {
    return {
      on: checkedColor,
      off: variant === "success" ? theme.colors.error : theme.colors.textMuted,
    };
  }
  if (variant === "success") {
    return { on: theme.colors.success, off: theme.colors.error };
  }
  if (variant === "error") {
    return { on: theme.colors.error, off: theme.colors.border };
  }
  if (variant === "info") {
    return { on: theme.colors.info, off: theme.colors.textMuted };
  }
  if (variant === "accent") {
    return { on: theme.colors.purple, off: theme.colors.textMuted };
  }
  return { on: theme.colors.purple, off: theme.colors.border };
}

const THUMB_PX = 22;
const LABEL_TRACK_DEFAULT_WIDTH = 100;
const LABEL_TRACK_HEIGHT = 28;

/**
 * ToggleSwitch — shared MUI Switch with V2 theme presets, optional labeled pill (Active / Inactive).
 *
 * @param {Object} props
 * @param {boolean} props.checked - Controlled checked state
 * @param {import('react').ChangeEventHandler<HTMLInputElement>} [props.onChange]
 * @param {boolean} [props.disabled]
 * @param {'small'|'medium'} [props.size] - default `small` (dense tables)
 * @param {'accent'|'success'|'error'|'info'|'default'} [props.variant] - theme preset; default `accent`
 * @param {string} [props.checkedColor] - override checked thumb / labeled “on” track
 * @param {string} [props.trackColor] - override checked track (plain Switch only)
 * @param {string} [props.inactiveTrackColor] - labeled mode: background when unchecked (optional)
 * @param {boolean} [props.showTrackLabels] - wide pill with text inside the track (e.g. Active / Inactive)
 * @param {string} [props.checkedLabel] - text when checked (default `Active`)
 * @param {string} [props.uncheckedLabel] - text when unchecked (default `Inactive`)
 * @param {number} [props.labeledTrackWidth] - pill width in px (default 100)
 * @param {Object} [props.sx] - merged after preset styles (plain Switch only)
 * @param {Object} [props.inputProps] - aria-label etc.; for labeled mode merged onto the button
 * @param {string} [props.id]
 * @param {string} [props.name]
 * @param {string} [props.className]
 * @param {boolean} [props.defaultChecked] - uncontrolled initial (plain Switch only)
 */
const ToggleSwitch = forwardRef(function ToggleSwitch(
  {
    checked,
    onChange,
    disabled = false,
    size = "small",
    variant = "accent",
    checkedColor,
    trackColor,
    inactiveTrackColor,
    sx,
    inputProps,
    id,
    name,
    className,
    defaultChecked,
    showTrackLabels = false,
    checkedLabel = "Active",
    uncheckedLabel = "Inactive",
    labeledTrackWidth = LABEL_TRACK_DEFAULT_WIDTH,
    ...rest
  },
  ref,
) {
  const mergedSx = useMemo(
    () => buildToggleSx({ variant, checkedColor, trackColor, sx }),
    [variant, checkedColor, trackColor, sx],
  );

  const palette = useMemo(
    () => getLabeledTrackPalette({ variant, checkedColor, inactiveTrackColor }),
    [variant, checkedColor, inactiveTrackColor],
  );

  const handleLabeledClick = (e) => {
    if (disabled) return;
    const synthetic = {
      ...e,
      target: { ...e.target, checked: !checked, name, id },
      currentTarget: e.currentTarget,
    };
    onChange?.(synthetic);
  };

  if (showTrackLabels) {
    const trackBg = checked ? palette.on : palette.off;
    const thumbLeft = checked ? `calc(100% - ${THUMB_PX}px - 3px)` : "3px";

    return (
      <Box
        ref={ref}
        component="button"
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={inputProps?.["aria-label"]}
        id={id}
        disabled={disabled}
        className={className}
        onClick={handleLabeledClick}
        sx={{
          width: labeledTrackWidth,
          minWidth: labeledTrackWidth,
          height: LABEL_TRACK_HEIGHT,
          borderRadius: "20px",
          bgcolor: trackBg,
          border: "none",
          padding: "4px",
          cursor: disabled ? "default" : "pointer",
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          boxSizing: "border-box",
          transition: "background-color 0.3s ease",
          opacity: disabled ? 0.5 : 1,
          verticalAlign: "middle",
          "&:focus-visible": {
            outline: `2px solid ${theme.colors.purple}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: thumbLeft,
            width: THUMB_PX,
            height: THUMB_PX,
            borderRadius: "50%",
            bgcolor: "#fff",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            transition: "left 0.3s ease",
            zIndex: 1,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            /* Unchecked: sit right of thumb (3 + 22px) + gap; extra pl insets label from track edge */
            left: checked ? 6 : 3 + THUMB_PX + 8,
            pl: checked ? 0 : "6px",
            fontSize: theme.typography.fontSize.xs,
            fontWeight: theme.typography.fontWeight.bold,
            color: "#fff",
            lineHeight: 1,
            opacity: checked ? 0 : 1,
            transition: "opacity 0.25s ease, left 0.25s ease",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 0,
          }}
        >
          {uncheckedLabel}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: checked ? 20 : 6,
            fontSize: theme.typography.fontSize.xs,
            fontWeight: theme.typography.fontWeight.bold,
            color: "#fff",
            lineHeight: 1,
            opacity: checked ? 1 : 0,
            transition: "opacity 0.25s ease, left 0.25s ease",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 0,
          }}
        >
          {checkedLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <Switch
      ref={ref}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      size={size}
      className={className}
      id={id}
      name={name}
      defaultChecked={defaultChecked}
      inputProps={inputProps}
      sx={mergedSx}
      {...rest}
    />
  );
});


ToggleSwitch.displayName = "ToggleSwitch";

export default ToggleSwitch;
